import { useCallback, useEffect, useSyncExternalStore } from "react";
import generateMembership from "../membership/generateMembership";
import { ClubMode, ClubOption, fetchUserClubs, fetchUserId } from "./clubApi";

export type UserClubsState =
  | { status: "loading" }
  | { status: "noAccount" }
  | {
      status: "ready";
      userId: number;
      clubs: ClubOption[];
      waitlistClubs: ClubOption[];
    };

const LOADING: UserClubsState = { status: "loading" };

// Shared between the top-bar club switcher and the page content so both see the
// same list without refetching on every navigation.
const cache = new Map<string, UserClubsState>();
const inflight = new Map<string, Promise<UserClubsState>>();
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function resolveUserId(
  devPort: string,
  userName: string,
  mode: ClubMode
): Promise<number | null> {
  const id = await fetchUserId(devPort, userName, mode);
  if (id !== null || mode === "manager") {
    return id;
  }
  // Managers switching to player mode get a player membership on first use.
  const managerId = await fetchUserId(devPort, userName, "manager");
  if (managerId === null) {
    return null;
  }
  await generateMembership(devPort, userName, 0, undefined);
  return fetchUserId(devPort, userName, "player");
}

function loadUserClubs(
  devPort: string,
  userName: string,
  mode: ClubMode
): Promise<UserClubsState> {
  const key = devPort + "|" + userName + "|" + mode;
  const pending = inflight.get(key);
  if (pending) {
    return pending;
  }
  const request = resolveUserId(devPort, userName, mode)
    .then(async (userId): Promise<UserClubsState> => {
      if (userId === null) {
        return { status: "noAccount" };
      }
      const { clubs, waitlistClubs } = await fetchUserClubs(devPort, userId, mode);
      return { status: "ready", userId, clubs, waitlistClubs };
    })
    .then((state) => {
      cache.set(key, state);
      notify();
      return state;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, request);
  return request;
}

/** Drop cached club lists, e.g. after the user joins or registers a club. */
export function invalidateUserClubs() {
  cache.clear();
  notify();
}

function useUserClubs(devPort: string, userName: string, mode: ClubMode) {
  const key = devPort + "|" + userName + "|" + mode;
  const state = useSyncExternalStore(subscribe, () => cache.get(key) ?? LOADING);

  useEffect(() => {
    if (userName && !cache.has(key)) {
      loadUserClubs(devPort, userName, mode);
    }
  }, [key, devPort, userName, mode, state]);

  const reload = useCallback(() => {
    cache.delete(key);
    return loadUserClubs(devPort, userName, mode);
  }, [key, devPort, userName, mode]);

  return { state, reload };
}

export default useUserClubs;
